import { z } from "zod";

import { CUSTOMER_SEGMENTS } from "./analytics.js";
import { isNotificationActionUrl } from "./notifications.js";

// ---- storefront banners ----------------------------------------------------------------------
// A banner is a scheduled campaign of slides on an environment's storefront (docs/storefront.md).
// Tenant editors write them in the dashboard (Storefront → Banners); the BFF serves the ones live
// for the visiting shopper's spend band.

/**
 * Who a banner is shown to: a signed-out visitor, or a signed-in shopper's customer segment
 * (trailing 30-day spend, `customerSegment.ts` in core), lowest first. A banner that names none
 * is shown to everyone.
 */
export const BANNER_SPEND_BANDS = ["signed_out", ...CUSTOMER_SEGMENTS] as const;
export type BannerSpendBand = (typeof BANNER_SPEND_BANDS)[number];
export const bannerSpendBandSchema = z.enum(BANNER_SPEND_BANDS);

/** The most enabled banners one environment may have live at the same moment. */
export const BANNER_MAX_LIVE = 5;
/** The most slides one banner carries. */
export const BANNER_MAX_SLIDES = 10;
export const BANNER_NAME_MAX = 100;
export const BANNER_ALT_TEXT_MAX = 200;
/** A slide's words are drawn in full, never cut: these keep them to about three lines each. */
export const BANNER_TITLE_MAX = 40;
export const BANNER_SUBTITLE_MAX = 120;
export const BANNER_ACTION_LABEL_MAX = 40;
const BANNER_IMAGE_URL_MAX = 2000;
const BANNER_ACTION_URL_MAX = 500;

/** A slide's call to action: a button label and where it goes. */
export const bannerActionSchema = z.object({
  label: z.string().trim().min(1).max(BANNER_ACTION_LABEL_MAX),
  url: z
    .string()
    .trim()
    .min(1)
    .max(BANNER_ACTION_URL_MAX)
    .refine(isNotificationActionUrl, "Use a storefront path like /boxes or an https:// link."),
});
export type BannerAction = z.infer<typeof bannerActionSchema>;

/**
 * One slide, as stored in `storefront_banners.slides` and as the storefront receives it: an
 * optional title and the subtitle under it (plain text, null = none, and null on a slide saved
 * before slides had words), art for wide screens and for phones (null = none, a slide of words
 * alone), alt text for the art ("" = decorative) and an optional call to action. Which of these a
 * slide must have is `bannerSlideContentIssues`, checked on save only, so this reads every
 * stored row.
 */
export const bannerSlideSchema = z.object({
  id: z.string().min(1).max(64),
  title: z.string().trim().min(1).max(BANNER_TITLE_MAX).nullable().default(null),
  subtitle: z.string().trim().min(1).max(BANNER_SUBTITLE_MAX).nullable().default(null),
  desktopImageUrl: z.url().max(BANNER_IMAGE_URL_MAX).nullable(),
  mobileImageUrl: z.url().max(BANNER_IMAGE_URL_MAX).nullable(),
  altText: z.string().trim().max(BANNER_ALT_TEXT_MAX),
  action: bannerActionSchema.nullable(),
});
export type BannerSlide = z.infer<typeof bannerSlideSchema>;

/** The fields a slide's content rules read, blank as null. */
export type BannerSlideContentFields = Pick<
  BannerSlide,
  "title" | "subtitle" | "desktopImageUrl" | "mobileImageUrl"
>;

/** The fields a content rule can refuse, each with what to do about it. */
export type BannerSlideContentIssues = Partial<
  Record<"desktopImageUrl" | "mobileImageUrl" | "subtitle", string>
>;

/**
 * What a slide's content rules refuse, field by field: its art comes as a wide and phone pair or
 * not at all, a slide without art needs a title to show, and a subtitle sits under a title. The
 * dashboard shows these under the fields, and saving refuses the same.
 */
export function bannerSlideContentIssues(
  slide: BannerSlideContentFields,
): BannerSlideContentIssues {
  const issues: BannerSlideContentIssues = {};
  const wide = Boolean(slide.desktopImageUrl);
  const phone = Boolean(slide.mobileImageUrl);
  if (wide && !phone) issues.mobileImageUrl = "Add the phone art too, or remove the wide art.";
  if (phone && !wide) issues.desktopImageUrl = "Add the wide art too, or remove the phone art.";
  if (!wide && !phone && !slide.title) {
    issues.desktopImageUrl = "Upload the wide art, paste its URL, or give the slide a title.";
    issues.mobileImageUrl = "Upload the phone art, paste its URL, or give the slide a title.";
  }
  if (slide.subtitle && !slide.title) issues.subtitle = "Add a title above it, or clear it.";
  return issues;
}

/**
 * A slide as the dashboard saves it: a new one has no id yet, and the server mints one. The
 * content rules are checked here, on the save, never on `bannerSlideSchema`, which every stored
 * row is read through.
 */
export const bannerSlideInputSchema = bannerSlideSchema
  .extend({ id: z.string().min(1).max(64).optional() })
  .superRefine((slide, ctx) => {
    for (const [field, message] of Object.entries(bannerSlideContentIssues(slide))) {
      ctx.addIssue({ code: "custom", path: [field], message });
    }
  });
export type BannerSlideInput = z.infer<typeof bannerSlideInputSchema>;

/**
 * A banner as the dashboard saves it, whole. `startsAt` null = live as soon as it is enabled;
 * `endsAt` null = until it is paused or deleted. `spendBands` empty = everyone.
 */
export const bannerInputSchema = z
  .object({
    name: z.string().trim().min(1).max(BANNER_NAME_MAX),
    enabled: z.boolean(),
    startsAt: z.iso.datetime({ offset: true }).nullable(),
    endsAt: z.iso.datetime({ offset: true }).nullable(),
    spendBands: z.array(bannerSpendBandSchema).max(BANNER_SPEND_BANDS.length),
    slides: z.array(bannerSlideInputSchema).min(1).max(BANNER_MAX_SLIDES),
  })
  .refine(
    (banner) =>
      banner.startsAt === null ||
      banner.endsAt === null ||
      Date.parse(banner.endsAt) > Date.parse(banner.startsAt),
    { path: ["endsAt"], message: "The end must be after the start." },
  );
export type BannerInput = z.infer<typeof bannerInputSchema>;

/** Where a banner stands at `now`: switched off, not started, running, or over. */
export type BannerState = "paused" | "scheduled" | "live" | "ended";

export function bannerState(
  banner: { enabled: boolean; startsAt: Date | string | null; endsAt: Date | string | null },
  now: Date,
): BannerState {
  if (!banner.enabled) return "paused";
  const at = now.getTime();
  if (banner.endsAt !== null && new Date(banner.endsAt).getTime() <= at) return "ended";
  if (banner.startsAt !== null && new Date(banner.startsAt).getTime() > at) return "scheduled";
  return "live";
}

/** A live banner as the storefront receives it: its slides, in order, and nothing staff-facing. */
export const storefrontBannerSchema = z.object({
  id: z.string(),
  slides: z.array(bannerSlideSchema),
});
export type StorefrontBanner = z.infer<typeof storefrontBannerSchema>;

export const storefrontBannersResponseSchema = z.object({
  banners: z.array(storefrontBannerSchema),
});
export type StorefrontBannersResponse = z.infer<typeof storefrontBannersResponseSchema>;

/**
 * What the BFF sends `POST /v1/storefront/banners`: the store's host, and the shopper's session
 * when there is one. No session, or one that no longer verifies, reads as signed out.
 */
export const storefrontBannersRequestSchema = z.object({
  host: z.string().min(1).max(255),
  sessionToken: z.string().min(1).max(256).optional(),
});
